import type { Translation } from "./types";

const en: Translation = {
    errors: {
        network: "Network error, please try again",
        tooLarge: "The pictures are too large to upload, please try smaller ones",
        unknown: "Something went wrong",
    },
    filesInput: {
        preparing: "Preparing pictures...",
    },
    actionsBar: {
        filters: "Filters",
        applyFilters: "Apply filters",
        clearFilters: "Clear filters",
        cancel: "Cancel",
    },
    languages: {
        ar: "العربية",
        en: "English",
    },
    home: {
        title: "Home",
        greetingMorning: "Good morning",
        greetingAfternoon: "Good afternoon",
        greetingEvening: "Good evening",
        menuLive: "Your menu is live at",
        copyLink: "Copy link",
        copied: "Copied",
        previewMenu: "Preview Menu",
        attentionTitle: "Needs your attention",
        allGoodTitle: "Everything looks good",
        allGoodSubtitle: "Nothing needs your attention right now",
        days: "days",
        statsTitle: "Your store at a glance",
        statProducts: "Products",
        statActiveNote: "active",
        statCategories: "Categories",
        statDiscounts: "Running discounts",
        statBranches: "Branches",
        statSlotsNote: "slots",
        statStaff: "Staff",
        statDaysLeft: "Days left",
        alertMenuEmpty: "Your menu has no products yet",
        alertProductsUnpriced: "Products with no price",
        alertCategoriesMissing: "You have products but no categories",
        alertCategoriesEmpty: "Categories with no products",
        alertProductsInactive: "Products hidden from customers",
        alertBranchSlotsUnused: "Paid branch slots not used yet",
        alertBranchesUnconfigured: "Branches missing their details",
        alertSubscriptionMissing: "Your store has no active subscription",
        alertSubscriptionPending: "Your payment is awaiting confirmation",
        alertSubscriptionPaused: "Your menu is paused — renew to bring it back",
        alertSubscriptionGrace: "Your subscription expired, renew before it pauses",
        alertSubscriptionExpiring: "Your subscription is about to expire",
        alertSupportContacted: "Our team replied to your ticket",
        retry: "Try again",
    },
    login: {
        title: "Welcome",
        subtitle: "Enter your phone number to access the system",
        phonePlaceholder: "Enter your phone number",
        acceptTermsBefore: "I agree to the",
        acceptTermsLink: "Terms & Conditions",
        readPrivacyBefore: "I have read the",
        readPrivacyLink: "Privacy Policy",
        consentRequired: "Please agree to both before continuing",
        submitButton: "Login",
    },
    verifyOtp: {
        title: "OTP Verification",
        subtitle: "Enter the 4-digit verification code sent to your phone",
        resendLink: "Resend code",
        resendHint: "Didn't receive a code?",
        submitButton: "Verify",
        reEnterPhone: "Re-enter phone number",
    },
    completeData: {
        title: "Welcome",
        subtitle: "Complete your data to enjoy the system",
        pictureLabel: "Picture",
        namePlaceholder: "Full name",
        submitButton: "Create account",
    },
    selectStore: {
        title: "Almost there",
        subtitle: "Choose the store you want or create a new one",
        selectButton: "Select",
        newStoreButton: "New store",
    },
    slugInfo: {
        storeTitle: "What is the 'Unique name'?",
        storeDefinition: "It's your non-changeable menu address.",
        storeQrNote: "QR codes are built with this link. it's non-changeable.",
        branchTitle: "What is the 'Branch URL'?",
        branchDefinition: "It's the name that opens this branch's own menu.",
        branchQrNote: "The branch QR codes you print are built with this link. it's non-changeable.",
        exampleLabel: "Example",
        exampleCaption: "This part is yours to choose",
        rulesTitle: "Rules",
        rules: [
            "English lowercase letters, numbers and hyphens ( - ) only",
            "Starts with a letter",
            "Ends with a letter or a number",
            "Between 3 and 30 characters",
            "No spaces, Arabic letters, or symbols like @ # !",
        ],
        button: "Got it",
    },
    createStore: {
        title: "One step left",
        subtitle: "Enter your store's data to enjoy the system",
        logoLabel: "Your logo",
        nameLabel: "Store name:",
        slugPlaceholder: "Unique name",
        slugHint: "Your menu URL:",
        slugInvalid: "Must start with a lowercase letter, be 3-30 characters of letters, numbers and hyphens, and end with a letter or number",
        taglineLabel: "Tagline:",
        typePlaceholder: "Store type",
        typeCafe: "Cafe",
        typeRestaurant: "Restaurant",
        typeBoth: "Cafe & Restaurant",
        typeOther: "Other",
        submitButton: "Create account",
        languagesTitle: "Choose store & menu language",
        languagesSubtitle: "Pick languages you will fill the menu with, you can add more later",
        languagesContinue: "Continue",
        languagesBack: "Back",
    },
    menu: {
        categories: "Categories",
        addLabel: "Add",
        productCount: "products",
        emptyCategoriesTitle: "No categories yet",
        emptyCategoriesSubtitle: "Add your first category to the menu",
        emptyProductsTitle: "No products yet",
        emptyProductsSubtitle: "Add your first product to this category",
        discount: "Discount",
        visibleLabel: "Hidden?",
        availableLabel: "Active?",
    },
    sortCategories: {
        title: "Sort Categories",
        save: "Save Order",
        reset: "Reset",
    },
    sortProducts: {
        title: "Sort Products",
        save: "Save Order",
        reset: "Reset",
    },
    formCategory: {
        createTitle: "Add Category",
        editTitle: "Edit Category",
        nameLabel: "Name:",
        submitCreate: "Add",
        submitEdit: "Save Changes",
        deleteMessage: "Delete this category? Products linked to it will not be deleted.",
        deleteConfirm: "Delete",
        editAction: "Edit",
        deleteAction: "Delete",
    },
    notFound: {
        title: "Page Not Found",
        subtitle: "Sorry, the page you're looking for doesn't exist or has been moved",
        backButton: "Back to Home",
    },
    profilePopup: {
        owner: "Owner",
        staff: "Staff",
        logout: "Logout",
        switchTitle: "Accounts",
        otherAccounts: "Other accounts",
        addAccount: "Add another account",
        switchConfirmMessage: "Switch to",
        switchConfirmAction: "Switch account",
        switchCancel: "Cancel",
        viewProfile: "View profile",
    },
    switchStorePopup: {
        createStore: "Create a new store",
    },
    formProduct: {
        createTitle: "Add Product",
        editTitle: "Edit Product",
        nameSection: "Name",
        descriptionSection: "Description",
        categoriesSection: "Categories",
        categoriesVisibilitySection: "Categories and Visibility",
        pricesSection: "Prices",
        addPrice: "Add Price",
        priceAmount: "Amount",
        priceLabel: "Label",
        showLabel: "Show Label",
        visibilitySection: "Visibility",
        activeLabel: "Active",
        activeHint: "Is it visible?",
        hiddenLabel: "Hidden",
        hiddenHint: "Is it hidden?",
        discountSection: "Discount",
        discountType: "Discount Type",
        discountPercentage: "Percentage %",
        discountFixed: "Fixed Amount",
        discountAmount: "Discount Value",
        pictureSection: "Picture",
        addPicture: "Add Picture",
        takePicture: "Take a picture",
        capturePicturesAction: "Capture Pictures",
        picturesLimitHint: "8 pictures max",
        emptyPrices: "No prices yet",
        emptyPictures: "No pictures yet",
        nameRequired: "A name is required",
        categoryRequired: "At least one category is required",
        editAction: "Edit",
        deleteAction: "Delete",
        duplicateAction: "Duplicate",
        deleteMessage: "Delete this product? This action cannot be undone.",
        deleteConfirm: "Delete",
        cancel: "Cancel",
        submit: "Add",
        submitEdit: "Save Changes",
    },
    pricePopup: {
        createTitle: "Add Price",
        editTitle: "Edit Price",
        save: "Save",
        cancel: "Cancel",
    },
    clients: {
        title: "Staff",
        addLabel: "Add",
        emptyTitle: "No staff yet",
        emptySubtitle: "Add your first staff member",
        ownerBadge: "Owner",
        permissionsLabel: "permissions",
        allBranches: "All Branches",
    },
    formClient: {
        createTitle: "Add Staff",
        editTitle: "Edit Staff",
        editPermissionsTitle: "Edit Permissions",
        editAction: "Edit",
        deleteAction: "Remove",
        deleteMessage: "Remove this staff member from the store?",
        deleteConfirm: "Remove",
        basicInfoSection: "Basic Information",
        name: "Name",
        namePlaceholder: "Full name",
        phone: "Phone Number",
        phonePlaceholder: "01xxxxxxxxx",
        genderMale: "Male",
        genderFemale: "Female",
        accessSection: "Access",
        makeOwnerLabel: "Owner Permissions",
        makeOwnerHint: "Full access to the store",
        permissionsSection: "Permissions",
        modelColumn: "Section",
        view: "View",
        edit: "Edit",
        branchesSection: "Branches",
        branchesAll: "All Branches",
        branchesSome: "Specific Branches",
        cancel: "Cancel",
        submit: "Add Staff",
        submitEdit: "Save Changes",
    },
    branches: {
        title: "Branches",
        purchaseLabel: "Purchase new branch",
        infoNote: "Branches let you customize the menu per branch — disable products or change prices for each branch individually.",
        emptyTitle: "No branches",
        emptySubtitle: "You haven't added any branches yet",
        slugLabel: "slug",
        addressLabel: "Address",
        inActiveLabel: "unavailable",
        hiddenLabel: "hidden",
        pricesLabel: "price override",
        resetLabel: "Reset",
        detailsLabel: "Details",
        slotName: "Branch",
        addBranchData: "Add branch data",
        unconfiguredNote: "No details have been added for this branch yet",
        noOverridesLabel: "No menu overrides",
        editAction: "Edit branch",
        resetAction: "Reset to default",
        deleteAction: "Delete branch",
        deleteConfirmMessage: "Branch data will be permanently deleted. The branch slot will remain in your subscription.",
        deleteConfirmLabel: "Delete",
        resetConfirmMessage: "All customizations for this branch (prices, hidden and unavailable products) will be removed. Are you sure?",
        resetConfirmLabel: "Reset",
    },
    formBranch: {
        createTitle: "Add Branch",
        editTitle: "Edit Branch",
        nameLabel: "Branch name",
        slugLabel: "Branch URL",
        slugPlaceholder: "maadi",
        slugHint:
            "Used in the branch URL. Must start with a lowercase letter, be 3-30 characters of letters, numbers and hyphens, and end with a letter or number",
        addressLabel: "Address (optional)",
        submit: "Save",
        submitEdit: "Save Changes",
        mapsUrlPlaceholder: "https://maps.app.goo.gl/...",
        mapsUrlHint: "Paste the share link from Google Maps so customers can find this branch",
        addressPlaceholders: {
            ar: "شارع 9، المعادي، القاهرة",
            en: "Street 9, Maadi, Cairo",
        },
    },
    support: {
        title: "Support",
        addLabel: "New Ticket",
        infoNote:
            "Tell us about any problem or suggestion and our team will contact you on your phone number. You can delete a ticket once your issue is resolved.",
        emptyTitle: "No tickets",
        emptySubtitle: "Open a ticket and our team will get back to you",
        loading: "Loading tickets…",
        statusPending: "Under review",
        statusContacted: "We contacted you",
        statusSolved: "Solved",
        readMore: "Read more",
        readLess: "Show less",
        deleteAction: "Delete ticket",
        deleteMessage: "Delete this ticket? You will no longer see it in your list.",
        deleteConfirm: "Delete",
    },
    formSupport: {
        createTitle: "New Ticket",
        titleSection: "Subject",
        titlePlaceholder: "Briefly, what is the problem?",
        messageSection: "Details",
        messagePlaceholder: "Explain the problem in detail so we can help you faster",
        contactNote: "Our team will contact you on your account phone number.",
        cancel: "Cancel",
        submit: "Send Ticket",
    },
    bottomBar: {
        more: "More",
        close: "Close",
    },
    previewMenu: {
        title: "Menu preview",
        loading: "Loading your menu...",
    },
    menuEditor: {
        reorder: "Reorder",
        saveBeforeReorder: "Save your changes before reordering",
        price: "Price",
        priceLabel: "Label",
        title: "Menu editor",
        prices: "Prices",
        pricesOf: "Price tiers",
        noPricesYet: "No price yet",
        addPrice: "Add price",
        showLabel: "Show label",
        discount: "Discount",
        noDiscount: "None",
        discountOn: "Discount is on",
        discountOff: "No discount",
        discountPercentage: "Percentage",
        discountFixed: "Fixed",
        discountAmount: "Amount",
        fixRowsFirst: "Some rows still need a name, and products need a category",
        categories: "Categories",
        products: "Products",
        categoriesOf: "Product categories",
        noCategoriesYet: "Add a category first",
        active: "Active",
        hidden: "Hidden",
        pickCategories: "Choose",
        filterByCategories: "Filter by categories",
        clearFilter: "Clear filter",
        noRowsForFilter: "No products in the chosen categories",
        addRow: "Add row",
        discard: "Discard",
        save: "Save",
        loading: "Loading the menu...",
        retry: "Try again",
        newBadge: "new",
        rowTools: "Row actions",
        revertRow: "Undo this row",
        removeRow: "Remove this row",
        cancel: "Cancel",
        done: "Done",
        nameColumn: "Name",
        descColumn: "Description",
    },
    sideBar: {
        home: "Home",
        menu: "Menu",
        branches: "Branches",
        orders: "Orders",
        clients: "Clients",
        subscriptions: "Subscription",
        settings: "Settings",
        support: "Support",
        profile: "Profile",
    },
    branchMenu: {
        activeHereLabel: "Active here?",
        hiddenHereLabel: "Hidden here?",
        previewMenuLabel: "Preview Menu",
        configureLabel: "Set Up in Subscription",
        configureNote: "This branch hasn't been set up yet",
        resetLabel: "Reset",
        editForBranchLabel: "Edit for this branch",
        resetProductLabel: "Reset product",
        settingsTitle: "Branch settings",
        saveLabel: "Save",
        cancelLabel: "Cancel",
        priceHiddenBadge: "Hidden here",
        labelHiddenBadge: "Label hidden",
        discountRemovedBadge: "Discount removed here",
        changedCountLabel: "modified",
        viewChangesLabel: "View differences",
        diffTitle: "Branch changes",
        diffNoChanges: "No changes for this product at this branch",
        diffInactiveLabel: "Inactive here",
        editPricesTitle: "Edit branch prices",
        originalPriceLabel: "Base price",
    },
    branchAddon: {
        title: "Add Branches",
        subtitle: "Add new branches to your current subscription",
        currentPlanLabel: "Current Plan",
        branchesLabel: "Current Branches",
        endsLabel: "Ends",
        comingPlanLabel: "Upcoming Renewal",
        branchCountLabel: "Branches to Add",
        activeAmountLabel: "Current period (prorated)",
        comingAmountLabel: "Upcoming renewal",
        totalLabel: "Total",
        egp: "EGP",
        submitButton: "Confirm Request",
        amountLabel: "Amount Due",
        paymentSubtitle: "Transfer the amount using any method below, then send your receipt along with your store URL via WhatsApp",
        sendReceiptButton: "Send Receipt via WhatsApp",
        successNote: "We'll review your receipt and activate the branches in less than a day",
        copyPhone: "Copy",
        copied: "Copied",
        addBranchesButton: "Add branches",
        pendingAddonNote: "A branch add-on request is under review",
        pendingAddonTitle: "Branch Add-on Request",
        payNowButton: "Payment Details",
        requestedAtLabel: "Requested",
        storeSlugLabel: "Your Store URL",
        storeSlugNote: "Send this URL with your receipt so we can identify your store",
    },
    subscriptions: {
        title: "Subscription",
        statusActive: "Active",
        statusGrace: "Grace Period",
        statusPaused: "Paused",
        statusPending: "Awaiting Activation",
        statusComing: "Upcoming Renewal",
        periodMonthly: "Monthly",
        periodQuarterly: "Quarterly",
        periodAnnually: "Annually",
        perMonth: "/ mo",
        perQuarter: "/ 3 mo",
        perYear: "/ yr",
        choosePlanTitle: "Start with Menuket",
        choosePlanSubtitle: "Choose your plan and start your journey now",
        saveLabel: "Save",
        couponLabel: "Coupon Code",
        couponPlaceholder: "Enter coupon code (optional)",
        couponApply: "Apply",
        couponRemove: "Remove",
        couponSavedLabel: "You save",
        couponDiscountLabel: "Discount",
        payWindowTitle: "Pay within 2 days",
        payWindowNote:
            "Send your transfer receipt before {date}. After that this request is canceled automatically and you will need to start again.",
        totalLabel: "Total",
        egp: "EGP",
        subscribeButton: "Subscribe Now",
        branchCount: "branch",
        branchesLabel: "Number of Branches",
        branchAddonLabel: "per extra branch",
        periodLabel: "Plan",
        pendingTitle: "Awaiting Activation",
        pendingSubtitle: "Transfer the amount using any method below, then send your receipt along with your store URL via WhatsApp",
        storeSlugLabel: "Your store URL",
        storeSlugNote: "Send this URL with your receipt so we can identify your store",
        amountLabel: "Amount Due",
        copyPhone: "Copy",
        copied: "Copied",
        sendReceiptButton: "Send Receipt via WhatsApp",
        pendingNote: "We'll review your receipt and activate your subscription in less than a day",
        currentPlanTitle: "Current Plan",
        startDateLabel: "Started",
        endDateLabel: "Ends",
        daysLeft: "days left",
        renewButton: "Renew Subscription",
        comingTitle: "Upcoming Renewal",
        comingNote: "Will activate automatically when your current plan ends",
        graceTitle: "Your Subscription Has Expired!",
        graceNote: "You have only",
        graceDays: "days to renew before your menu goes offline permanently",
        renewNow: "Renew Now",
        pausedTitle: "Menu is Paused",
        pausedNote: "Customers can't see your menu right now",
        reactivate: "Reactivate Subscription",
        pendingRenewalTitle: "Renewal Under Review",
        pendingRenewalNote: "Your renewal will activate once we review your transfer receipt",
        planPrice: "Plan Price",
        comingStartLabel: "Starts on",
        comingEndLabel: "Valid until",
        graceDaysLabel: "days remaining",
        graceEndedLabel: "Subscription ended on",
        graceOffLabel: "Menu goes offline on",
        branchConfigButton: "Branch Config",
        branchConfigTitle: "Branch Config",
        confirmTitle: "Confirm your subscription",
        confirmSubtitle: "Review what you are subscribing to before we create it.",
        confirmExtraBranchesLabel: "Extra branches",
        confirmPaymentTitle: "How payment works",
        confirmPaymentNote:
            "Confirming creates a subscription awaiting activation. You then transfer the total via InstaPay, My Fawry or Vodafone Cash and send the receipt over WhatsApp — we activate it in less than a day.",
        confirmSubmitButton: "I Understand",
        confirmCancelButton: "Cancel",
    },
    profile: {
        title: "Profile",
        ownerBadge: "Store owner",
        staffBadge: "Staff",
        phoneLabel: "Phone number",
        genderLabel: "Gender",
        genderMale: "Male",
        genderFemale: "Female",
        genderUnknown: "Not set",
        editProfileAction: "Edit Details",
        otherStores: "Other stores",
        switchStoreAction: "Open store",
        switchStoreConfirmMessage: "Switch to",
        switchStoreConfirmAction: "Switch store",
        storesTitle: "Your stores",
        storesSubtitle: "Your access differs from store to store",
        activeStoreBadge: "Current store",
        permissionsCount: "permissions",
        ownerFullAccess: "Full access",
        permissionsTitle: "Your access in the current store",
        permissionsSubtitle: "What you can do here",
        noPermissions: "No permissions in this store",
        branchesTitle: "Branches you can reach",
        branchesSubtitle: "Your scope inside this store",
        allBranches: "All branches",
        noBranches: "No branches assigned to you",
        actionView: "View",
        actionEdit: "Edit",
        logoutTitle: "Account",
        logoutAction: "Log out",
        logoutConfirmMessage: "Log out of this account? You will need a fresh verification code to get back in.",
        logoutConfirmAction: "Yes, log me out",
        logoutCancel: "Cancel",
        logoutSwitchHint: "Or switch to another account without logging out:",
        logoutAddAccount: "Add another account instead",
    },
    qrCodes: {
        title: "QR codes",
        subtitle: "Print it once, and update the menu anytime",
        closeAction: "Close",
        storeTarget: "Main menu",
        noBranches: "No branches yet — this code opens your main menu",
        noStore: "Select a store first to get its QR code",
        styleClassic: "Classic",
        styleRounded: "Rounded",
        styleDots: "Dots",
        styleLeaf: "Leaf",
        copyAction: "Copy link",
        downloadLabel: "Download",
        formatHint: "SVG for printing at any size without blurring.",
    },
    orders: {
        title: "Orders",
        emptyTitle: "No orders yet",
        emptySubtitle: "Orders your customers send from the menu will show up here",
        filterAll: "All",
        orderNumber: "Order",
        itemsCount: "items",
        totalLabel: "Total",
        tableLabel: "Table",
        addressLabel: "Address",
        branchLabel: "Branch",
        mainMenu: "Main menu",
        callCustomer: "Call customer",
        confirmLabel: "Confirm",
        rejectLabel: "Reject",
        cancelLabel: "Cancel order",
        editTitle: "Edit order",
        editLabel: "Edit",
        editPrompt: "Save these changes to order #{number}?",
        editConfirmLabel: "Save changes",
        editSaveLabel: "Save",
        editSavingLabel: "Saving...",
        editCancelLabel: "Cancel",
        editNote: "A delivery address is kept if you switch the order to pickup or a table, so you can switch it back without typing it again.",
        editedBadge: "Edited",
        itemsTitle: "Items",
        addItemLabel: "Add item",
        removeItemLabel: "Remove item",
        itemsRequired: "An order has to keep at least one item",
        estimateLabel: "Estimated total",
        itemsNote: "Prices are taken from the menu when you save, so the total above is an estimate until then.",
        pickItemTitle: "Pick from the menu",
        pickItemSearch: "Search by product name",
        pickItemLoading: "Loading the menu...",
        pickItemEmpty: "No matching products",
        pickItemDone: "Done",
        pickedCount: "{count} items",
        newTitle: "New order",
        createLabel: "Save the order",
        statusLabel: "Order status",
        progressTitle: "Where it is",
        progressHint: "Tap a step to move the order to it",
        customerTitle: "Customer",
        returningBadge: "Regular",
        itemsFrom: "Items from {menu}",
        copyPhone: "Copy",
        copiedPhone: "Copied",
        callPhone: "Call",
        whatsappPhone: "WhatsApp",
        branchAll: "Every menu",
        searchNumber: "Order number",
        searchPhone: "Phone number",
        appliedTitle: "Filtered by",
        filterStatusLabel: "Status",
        filterBranchLabel: "Menu",
        filterDateLabel: "When",
        clearFiltersLabel: "Clear filters",
        clearFiltersPrompt: "Clear every filter and go back to all orders?",
        clearFiltersConfirm: "Clear them",
        firstOrderLabel: "First order with us",
        spentLabel: "Spent with us",
        acrossOrders: "across {count} orders",
        editedNote: "This order was edited {count} times after it was sent",
        moreItems: "+{count} more items",
        moreActions: "Actions",
        previewLabel: "Open order",
        /* --- New orders and updates arriving without a page refresh --- */
        liveLabel: "Live",
        liveOffLabel: "Not live",
        liveOnHint: "New orders are heard and announced here",
        liveOffHint: "Nothing is heard — the list changes only when you refresh it",
        newOrdersOne: "1 new order",
        newOrdersMany: "{count} new orders",
        newOrdersUnknown: "There may be new orders",
        changedNote: "and some orders changed",
        refreshLabel: "Refresh",
        dismissLabel: "Dismiss",
        changedBadge: "Changed",
        changedHint: "This order left the current filters after it changed — refresh the list",
        soundPrompt: "Play a sound for every new order? It helps when the screen is across the room.",
        soundConfirm: "Play a sound",
        soundDecline: "No sound",
        soundOnHint: "New orders make a sound",
        soundOffHint: "Sound is off — tap to turn it on",
        dateAll: "All time",
        dateToday: "Today",
        dateWeek: "Last week",
        dateMonth: "Last month",
        dateYear: "Last year",
        searchPlaceholder: "Order number or phone",
        statusAll: "Any status",
        phoneLabel: "Phone number",
        typeLabel: "Order kind",
        movePrompt: 'Move this order to "{status}"?',
        moveLabel: "Move",
        backPrompt: 'Put this order back to "{status}"?',
        backLabel: "Back",
        confirmPrompt: "Confirm this order?",
        rejectPrompt: "Reject this order?",
        cancelPrompt: "Cancel this order?",
        loadMore: "Load more",
        status: {
            pending: "New",
            confirmed: "Confirmed",
            preparing: "Preparing",
            ready: "Ready",
            delivering: "On the way",
            completed: "Completed",
            rejected: "Rejected",
            cancelled: "Cancelled",
        },
        type: {
            delivery: "Delivery",
            table: "At a table",
            pickup: "Pick up",
        },
    },
    settings: {
        tabs: {
            storeDetails: "Store Details",
            contact: "Contact & Links",
            ordering: "Online Orders",
            menu: "Menu Settings",
        },
        accountTitle: "Your Profile",
        accountSubtitle: "This is about you, not about the store",
        pictureLabel: "Profile picture",
        changePicture: "Change picture",
        removePicture: "Remove picture",
        nameLabel: "Name",
        namePlaceholder: "Enter your name",
        phoneLabel: "Phone number",
        phoneHint: "Your phone number is your account and cannot be changed",
        genderLabel: "Gender",
        genderPlaceholder: "Select gender",
        genderMale: "Male",
        genderFemale: "Female",

        brandTitle: "Store Details",
        brandSubtitle: "The name, link and description your customers see on the menu",
        logoLabel: "Store logo",
        coverLabel: "Cover picture",
        coverHint: "A wide photo of your place, shown behind your name when a customer opens your store details",
        storeNameLabel: "Store name",
        slugLabel: "Menu link",
        slugPlaceholder: "your-store",
        slugHint: "Your menu will live at",
        slugLockedHint: "This link is printed on your QR codes, so it cannot be changed here",
        taglineLabel: "Tagline",
        taglineHint: "One short line under your store name — what you are known for",
        aboutLabel: "About your store",
        aboutHint: "Shown when a customer taps your store name in the menu. Line breaks are kept as you type them",
        typeLabel: "Store type",
        typePlaceholder: "Select type",
        typeCafe: "Café",
        typeRestaurant: "Restaurant",
        typeBoth: "Café & Restaurant",
        typeOther: "Other",
        readOnlyBadge: "View only",

        menuColorLabel: "Menu color",
        menuColorHint: "The accent color customers see on your menu — buttons, prices and highlights",
        menuColorReset: "Back to default",
        menuColorInvalid: "Enter a hex color such as #DD0D17",
        menuThemesLabel: "Menu themes",
        menuThemesHint: "Keep one theme only and the menu always opens in it, with no theme switcher",
        menuThemeLight: "Light",
        menuThemeDark: "Dark",

        preferencesTitle: "Preferences",
        preferencesSubtitle: "These settings apply to your dashboard only",
        languageLabel: "Dashboard language",
        languageArabic: "العربية",
        languageEnglish: "English",
        themeLabel: "Dashboard theme",
        themeLight: "Light",
        themeDark: "Dark",
        comingSoon: "Coming soon",
        themeHint: "Dark mode is landing in an upcoming update",

        saveButton: "Save changes",
        savingButton: "Saving...",
        cancelButton: "Cancel",
        savedMessage: "Saved",
        noChangesHint: "No changes to save",
    },
    storeLinks: {
        title: "Contact & links",
        subtitle: "Shown to customers inside your menu, in this order",
        readOnlyBadge: "View only",
        empty: "No links yet — add a phone number or a social profile",
        addLabel: "Add link",
        editTitle: "Edit link",
        createTitle: "New link",
        typeLabel: "Link type",
        valueLabel: "Value",
        valuePhoneHint: "Include the country code, e.g. +20 109 015 2528",
        valueUrlHint: "Paste the full link to your page",
        labelLabel: "Button text",
        labelHint: "Leave empty to use the link type's own name",
        visibleLabel: "Show in menu",
        visibleHint: "Turn off to keep the link without showing it to customers",
        hiddenBadge: "Hidden",
        removeLabel: "Remove",
        moveUpLabel: "Move up",
        moveDownLabel: "Move down",
        saveLabel: "Save link",
        cancelLabel: "Cancel",
        valueRequired: "Enter the number or link first",
        maxReached: "You have reached the maximum number of links",
        autoSaveHint: "Changes here are saved as you make them",
        types: {
            phone: "Phone",
            whatsapp: "WhatsApp",
            facebook: "Facebook",
            instagram: "Instagram",
            tiktok: "TikTok",
            website: "Website",
            maps: "Location on maps",
            custom: "Other link",
        },
    },
    menuTheme: {
        title: "Menu theme",
        subtitle: "The design your customers see when they scan your QR code",
        readOnlyBadge: "View only",
        activeBadge: "Active",
        currentLabel: "Your menu",
        chooseLabel: "Try this theme",
        customizeLabel: "Customize",
        openPreviewLabel: "Open full preview",
        loadingThemes: "Loading themes...",
        noThemes: "No themes available yet",

        popupSubtitle: "Set it up here, then apply it to your menu",
        previewTitle: "Menu preview",
        previewLoading: "Building preview...",
        previewNote: "This is your own menu, with your products",
        modesLabel: "Appearance",
        modesHint: "Keep one only and the menu always opens in it, with no switcher for customers",
        modeLight: "Light",
        modeDark: "Dark",
        layoutsLabel: "Layout",
        layoutsHint: "Keep one only and every customer sees the menu that way",
        layoutGrid: "Grid",
        layoutList: "List",
        colorLabel: "Menu color",
        colorHint: "The accent color on your menu — prices, highlights and active tabs",
        colorReset: "Back to default",
        colorInvalid: "Enter a hex color such as #DD0D17",
        noColorHint: "This theme uses its own colors and ignores a custom one",
        applyLabel: "Apply to my menu",
        saveLabel: "Save changes",
        savingLabel: "Saving...",
        applyingLabel: "Applying...",
        expandPreviewLabel: "Full screen",
        closePreviewLabel: "Close preview",
        orderHint: "The first one you pick is the one your menu opens with",
        cancelLabel: "Cancel",
        appliedMessage: "Your menu theme is updated",
    },
    settingsOrdering: {
        title: "Online ordering",
        subtitle: "Let customers send you an order straight from the menu",
        readOnlyBadge: "View only",
        enableLabel: "Take orders from the menu",
        enableHint: "Turn this off and your menu stays a menu — nothing about ordering is shown",
        typesLabel: "Where the order goes",
        typesHint: "Pick at least one. Delivery asks for an address, a table asks for its number, and pickup asks for neither",
        typeDelivery: "Delivery",
        typeTable: "At a table",
        typePickup: "Pick up",
    },
    settingsLanguages: {
        title: "Menu languages",
        subtitle: "The languages your customers can read your menu in",
        readOnlyBadge: "View only",
        defaultBadge: "Default",
        setDefault: "Make default",
        remove: "Remove",
        statusDefault: "Your menu opens in this language",
        statusEnabled: "Available on your menu",
        statusDisabled: "Not on your menu",
        translating: "Translating your menu — {done} of {total} done",
        paused: "Paused until tomorrow — {done} of {total} done, we'll finish the rest on our own",
        finished: "Translation finished",
        failed: "Translation stopped before it finished",
        needsWords: "{count} items couldn't be translated — they need your own words",
        reviewNote:
            "This is a machine translation. Please review your store description and your branches' addresses — those usually need your own words.",
        add: "Add to menu",
        addConfirm: "We'll translate your whole menu into this language automatically. It may take a while, and you can edit anything afterwards.",
        addConfirmAction: "Add and translate",
        removeConfirm: "Your menu stops showing this language. Nothing you wrote is deleted — add it back any time and it returns.",
        removeConfirmAction: "Remove",
        cancel: "Cancel",
        lastOneHint: "Your menu needs at least one language",
        hint: "Customers see a language switcher only when you offer more than one",
    },
};

export default en;
